import { useEffect, useState, useMemo } from 'react';
import Select from 'react-select';
import { SuscripcionFormProps } from '../../types/forms';
import { ClienteSinBeneficiario } from '../../types/clientes';
import { useConfigurationsQuery, useClientsWithoutBeneficiaryQuery } from '../../services/hooks';
import { validateIban, formatIban } from '../../utils/ibanValidation';
import { AvisoPrecio, DesglosePrecio } from '../suscripciones/PrecioSuscripcion';

// El tipo de pago determina la suscripción que se crea (ver handleTipoPago en
// InscripcionCliente): los tres primeros dan pilates-domiciliacion y el resto
// pilates-otros-pagos. Por eso estas listas tienen que cuadrar con el mapa
// TIPOS_PAGO_POR_SUSCRIPCION de EditarSuscripcion, o se puede dar de alta un tipo
// de pago que luego la pantalla de edición no ofrece.
const todasLasOpcionesPago = ['Domiciliaciones', 'Pagador - Cliente', 'Pagador - No Cliente', 'Efectivo', 'Tarjeta'];

// Un menor siempre va por domiciliación: hay un representante detrás. Si ese
// representante es cliente del centro, "Pagador - Cliente"; si es externo,
// "Pagador - No Cliente". Las tres opciones crean una suscripción domiciliada.
const opcionesMenorEdad = ['Domiciliaciones', 'Pagador - Cliente', 'Pagador - No Cliente'];

const frequencyToWeeklySessions = (frequency: number | string) => {
    if (frequency === 'Ilimitada') {
        return -1;
    }

    return Number(frequency);
};

const euros = (n?: number) => (typeof n === 'number' && !Number.isNaN(n)
    ? `${n.toLocaleString('es-ES', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`
    : '—');

export const SuscripcionForm = ({
    register, 
    errors, 
    tipoPagoSeleccionado,
    onTipoPagoChange,
    frecuencia,
    setValue,
    onPagadorSelect,
    esMenorDeEdad = false,
    cotizacion,
    cotizando = false,
    selectorTarifas,
    precioFijo = false,
    puedeFijarPrecio = false,
    onPrecioFijoChange,
    importe,
    descuento,
}: SuscripcionFormProps) => {
    const esIlimitada = frecuencia === 'Ilimitada' || Number(frecuencia) === -1;

    // Determinar qué opciones mostrar según la edad
    const opcionesTipoPago = useMemo(() => {
        return esMenorDeEdad ? opcionesMenorEdad : todasLasOpcionesPago;
    }, [esMenorDeEdad]);

    // Estados para errores de validación IBAN
    const [ibanErrors, setIbanErrors] = useState({
        domiciliacion: '',
        pagadorExterno: ''
    });

    const { data: configurations } = useConfigurationsQuery();
    const { data: clientesSinBeneficiario } = useClientsWithoutBeneficiaryQuery();

    // Función para manejar cambios en campos IBAN (formateo en tiempo real)
    const handleIbanChange = (field: 'domiciliacion' | 'pagadorExterno') => (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        const formatted = formatIban(value);
        
        // Actualizar el valor en react-hook-form
        if (field === 'domiciliacion') {
            setValue('iban', formatted);
        } else {
            setValue('pagador.IBAN', formatted);
        }
        
        // Limpiar error si existe
        if (ibanErrors[field]) {
            setIbanErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    // Función para validar IBAN al perder el foco
    const validateIbanField = (value: string, field: 'domiciliacion' | 'pagadorExterno') => {
        const validation = validateIban(value);
        
        setIbanErrors(prev => ({
            ...prev,
            [field]: validation.isValid ? '' : (validation.error || 'Hay un error en el formato del IBAN')
        }));
    };

    // El importe lo calcula el back a partir de las plazas (cotizacion), con las
    // tarifas por nivel si las hay. Con precio fijo manda lo que escriba el admin.
    // La ilimitada no depende de las plazas: sigue saliendo de la tarifa general.
    useEffect(() => {
        if (precioFijo) return;
        if (!esIlimitada) {
            // shouldValidate: si no, el "El importe es requerido" de cuando estaba
            // vacío se queda en pantalla aunque ya haya importe.
            if (cotizacion?.ok) setValue('importeSuscripcion', cotizacion.total, { shouldValidate: true });
            return;
        }
        if (configurations && configurations.length > 0 && configurations[0].tarifas) {
            const weeklySessions = frequencyToWeeklySessions(frecuencia);
            const importe = configurations[0].tarifas.find(t => t.weeklySessions === weeklySessions)?.price;
            if (importe !== undefined) {
                setValue('importeSuscripcion', importe, { shouldValidate: true });
            }
        }
    }, [configurations, frecuencia, setValue, cotizacion, precioFijo, esIlimitada]);

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Datos de la Suscripción</h3>
                {esMenorDeEdad && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800 border border-yellow-300">
                        ⚠️ Cliente menor de edad
                    </span>
                )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700">Tipo de Pago *</label>
                    <select
                        {...register("tipoPago")}
                        onChange={(e) => onTipoPagoChange(e.target.value)}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                    >
                        <option value="">Seleccione tipo de pago</option>
                        {opcionesTipoPago.map((tipo) => (
                            <option key={tipo} value={tipo}>{tipo}</option>
                        ))}
                    </select>
                    {errors.tipoPago && <p className="mt-1 text-sm text-red-600">{errors.tipoPago.message}</p>}
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Descuento (€)</label>
                    <div className="flex items-center">
                        <input
                            type="number"
                            step="0.01"
                            {...register("descuento", { valueAsNumber: true })}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                        />
                        <span className="ml-2">€</span>
                    </div>
                </div>

                {tipoPagoSeleccionado === 'Domiciliaciones' && (
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700">IBAN (opcional)</label>
                        <input
                            type="text"
                            {...register("iban")}
                            onChange={handleIbanChange('domiciliacion')}
                            onBlur={(e) => validateIbanField(e.target.value, 'domiciliacion')}
                            className={`mt-1 block w-full rounded-md shadow-sm ${
                                ibanErrors.domiciliacion 
                                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                                    : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
                            }`}
                            placeholder="ES00 0000 0000 0000 0000 0000"
                        />
                        {ibanErrors.domiciliacion && (
                            <p className="mt-1 text-sm text-red-600">{ibanErrors.domiciliacion}</p>
                        )}
                    </div>
                )}

                {tipoPagoSeleccionado === 'Pagador - Cliente' && (
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Seleccionar Pagador *</label>
                        <Select
                            {...register("pagador_id")}
                            options={clientesSinBeneficiario?.map((cliente: ClienteSinBeneficiario) => ({
                                value: cliente.subscription_id,
                                label: `${cliente.nombre}`
                            }))}
                            onChange={(option) => {
                                setValue('pagador_id', option?.value);
                                if (option?.value) {
                                    onPagadorSelect?.(option.value);
                                }
                            }}
                            className="mt-1"
                            placeholder="Buscar cliente..."
                        />
                        {errors.pagador_id && (
                            <p className="mt-1 text-sm text-red-600">{errors.pagador_id.message}</p>
                        )}
                    </div>
                )}

                {tipoPagoSeleccionado === 'Pagador - No Cliente' && (
                    <div className="md:col-span-2 bg-gray-50 p-4 rounded">
                        <h4 className="font-medium mb-4">Datos del Pagador</h4>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Nombre del Pagador</label>
                                <input
                                    type="text"
                                    {...register("pagador.nombre")}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">DNI del Pagador</label>
                                <input
                                    type="text"
                                    {...register("pagador.DNI")}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Email del Pagador</label>
                                <input
                                    type="email"
                                    {...register("pagador.email")}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                                    placeholder="correo@ejemplo.com"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Teléfono del Pagador</label>
                                <input
                                    type="text"
                                    {...register("pagador.telefono")}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                                />
                            </div>
                            <div className="col-span-2">
                                <label className="block text-sm font-medium text-gray-700">IBAN del Pagador</label>
                                <input
                                    type="text"
                                    {...register("pagador.IBAN")}
                                    onChange={handleIbanChange('pagadorExterno')}
                                    onBlur={(e) => validateIbanField(e.target.value, 'pagadorExterno')}
                                    className={`mt-1 block w-full rounded-md shadow-sm ${
                                        ibanErrors.pagadorExterno 
                                            ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                                            : 'border-gray-300 focus:border-indigo-500 focus:ring-indigo-500'
                                    }`}
                                    placeholder="ES00 0000 0000 0000 0000 0000"
                                />
                                {ibanErrors.pagadorExterno && (
                                    <p className="mt-1 text-sm text-red-600">{ibanErrors.pagadorExterno}</p>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Precio: a la izquierda lo que se decide (tarifa, precio fijo), a
                la derecha el resultado. El importe solo es un campo editable con
                precio fijo; si no, lo calcula el back con las plazas. */}
            <div className="mt-6 border-t pt-4">
                <h4 className="text-md font-semibold text-gray-800 mb-3">Precio</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                        {!precioFijo && (
                            <p className="text-sm text-gray-500">
                                {esIlimitada
                                    ? 'Suscripción ilimitada: precio de la tarifa general.'
                                    : 'Se calcula con las plazas elegidas y las tarifas del centro.'}
                            </p>
                        )}
                        {!precioFijo && !esIlimitada && selectorTarifas}
                        {puedeFijarPrecio && onPrecioFijoChange && (
                            <label className="flex items-start gap-2 text-sm text-gray-700">
                                <input
                                    type="checkbox"
                                    checked={precioFijo}
                                    onChange={(e) => onPrecioFijoChange(e.target.checked)}
                                    className="mt-0.5"
                                />
                                <span>
                                    Precio fijo
                                    <span className="block text-xs text-gray-500">
                                        Importe a mano. No cambiará al añadir o quitar plazas ni al actualizar tarifas.
                                    </span>
                                </span>
                            </label>
                        )}
                        {precioFijo ? (
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Importe mensual *</label>
                                <div className="flex items-center">
                                    <input
                                        type="number"
                                        step="0.01"
                                        {...register("importeSuscripcion", { valueAsNumber: true })}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                                    />
                                    <span className="ml-2">€</span>
                                </div>
                            </div>
                        ) : (
                            <input type="hidden" {...register("importeSuscripcion", { valueAsNumber: true })} />
                        )}
                    </div>

                    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 self-start">
                        {cotizando && !precioFijo && <p className="text-xs text-gray-500 mb-2">Calculando importe…</p>}
                        {!precioFijo && !esIlimitada && <AvisoPrecio cotizacion={cotizacion} />}
                        {!precioFijo && !esIlimitada && cotizacion?.ok && cotizacion.usaTarifasNombradas && (
                            <div className="mb-2">
                                <DesglosePrecio desglose={cotizacion.desglose} />
                            </div>
                        )}
                        <div className="flex justify-between items-baseline gap-4 border-t border-gray-200 pt-2">
                            <span className="text-sm text-gray-600">Importe mensual{precioFijo ? ' (precio fijo)' : ''}</span>
                            <span className="text-2xl font-bold text-gray-900">{euros(importe)}</span>
                        </div>
                        {(descuento ?? 0) > 0 && typeof importe === 'number' && !Number.isNaN(importe) && (
                            <>
                                <div className="flex justify-between text-sm text-gray-600 mt-1">
                                    <span>Descuento</span>
                                    <span>− {euros(descuento)}</span>
                                </div>
                                <div className="flex justify-between text-sm font-semibold text-gray-900 mt-1">
                                    <span>Total a pagar</span>
                                    <span>{euros(Math.max(0, importe - (descuento ?? 0)))}</span>
                                </div>
                            </>
                        )}
                        {errors.importeSuscripcion && (
                            <p className="mt-2 text-sm text-red-600">{errors.importeSuscripcion.message}</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};